import { registerToken } from '@rsos/maps/providers/esri/identityManager/registerToken';
import { instance } from '@rsos/sinatra';
import { getAPIHost } from '@rsos/utils/metaTags';
import { PSAPInfo } from '../capstone/psapInfo/psapInfoSlice';
import { gisServiceSort } from './gisServiceSort';
import {
  GISIdentity,
  GISService,
  GISServiceTypes,
  GISStateLayer,
} from './gisServicesTypes';
import { getGISLayerID } from './layer-loaders/getGISLayerID';
import { loadLayers, loadLayer } from './layer-loaders/loadLayers';

/**
 * Extracts the ArcGIS server base URL from a full service URL.
 * e.g., "https://nenaeprc.geocomm.cloud/arcgis/rest/services/..." -> "https://nenaeprc.geocomm.cloud/arcgis"
 */
function extractArcGISServerUrl(serviceUrl: string): string | null {
  const arcgisIndex = serviceUrl.indexOf('/arcgis');
  if (arcgisIndex === -1) return null;
  return serviceUrl.substring(0, arcgisIndex + '/arcgis'.length);
}

/**
 * Check if a service URL is a GeoComm/NENA EPRC service that requires token auth.
 * These include geocomm.cloud and nenaeprc domains.
 */
function isGeocommOrNenaService(serviceUrl: string): boolean {
  const url = serviceUrl?.toLowerCase() || '';
  return url.includes('geocomm') || url.includes('nenaeprc');
}

/**
 * Register tokens for all GeoComm/NENA services with ESRI's IdentityManager.
 * This ensures authentication works for on-demand layer loading.
 */
function registerGeocommTokens(services: GISService[]): void {
  const registeredServers = new Set<string>();

  services.forEach(service => {
    if (service.token && isGeocommOrNenaService(service.gis_service_url)) {
      const serverUrl = extractArcGISServerUrl(service.gis_service_url);
      if (serverUrl && !registeredServers.has(serverUrl)) {
        registerToken(serverUrl, service.token);
        registeredServers.add(serverUrl);
      }
    }
  });
}

type FetchGISServicesResponse = {
  gis_identities: GISIdentity[];
};

export const getGISServices = (orgName: string) =>
  instance.get<FetchGISServicesResponse>(
    `${getAPIHost()}/v1/capstone/psaps/${orgName}/gis-services`,
    {
      headers: { retry: true },
    },
  );

function createPlaceholderLayer(service: GISService): GISStateLayer {
  return {
    id: service.gisLayerID,
    title: service.display_name,
    service,
    active: false,
    sublayers: [],
    loading: false,
    loaded: false,
  };
}

export const getLoadedGISServices = async (
  orgName: string,
  settings: PSAPInfo['map_layer_settings'] = {},
  shouldLoadLayers = true,
) => {
  const response = await getGISServices(orgName);
  const data = response.data as FetchGISServicesResponse;

  // PSAPs can choose to enable layers by default (see
  // /admin/data-source/map-layers). If a layer should be on by default, set
  // its auto_checked here, and it will be turned on after the layers are
  // loaded into state.
  data.gis_identities.forEach(gisID => {
    gisID.gis_services.forEach(service => {
      if (settings[service.id]) {
        service.auto_checked ||= settings[service.id].default_checked;
      }
      service.gisLayerID = getGISLayerID(service);
    });
  });

  const gisIdentities = mapGISIdentities(data.gis_identities || []);

  const services = mapGISServices(data.gis_identities || []);

  const basemapService = services.find(
    service => service.service_type === GISServiceTypes.basemap,
  );
  if (basemapService) {
    registerToken(
      basemapService.gis_service_url,
      basemapService.token as string,
    );
  }

  // Register geocomm tokens for all services with the base server URL.
  // This ensures on-demand layer loading works without authentication prompts.
  registerGeocommTokens(services);

  let layerMap: GISStateLayer[];
  if (shouldLoadLayers) {
    layerMap = await loadLayers(services);
    layerMap = layerMap.map(layer => ({
      ...layer,
      loaded: true,
      loading: false,
    }));
  } else {
    layerMap = services.map(createPlaceholderLayer);
  }

  return { gisIdentities, services, layerMap };
};

export const loadSingleLayer = async (
  service: GISService,
): Promise<GISStateLayer> => {
  const layers = await loadLayer(service);
  if (layers.length === 0) {
    return {
      id: service.gisLayerID,
      title: service.display_name,
      service,
      active: false,
      sublayers: [],
      loading: false,
      loaded: false,
    };
  }
  return {
    ...layers[0],
    loaded: true,
    loading: false,
  };
};

/**
 * Map the GIS identities from the `/gis-services` payload
 */
function mapGISIdentities(gisIdentities: GISIdentity[]) {
  const tokenFetchedAtMs = Date.now();

  return gisIdentities.map(identity => {
    const {
      arcgis_token: arcGISToken,
      gis_identity_url: gisIdentityUrl,
      gis_services: gisServices,
      token_expires_in: tokenExpiresIn,
    } = identity;

    return {
      arcGISToken,
      gisServices,
      gisIdentityUrl,
      tokenExpiresIn,
      tokenFetchedAtMs,
    };
  });
}
/**
 * Map the GIS services to a flat sorted array based on the display_order property from the
 * /gis-services payload.
 */
function mapGISServices(gisIdentities: GISIdentity[]) {
  let gisServices = [];
  let layerIndex = 1;

  gisServices = gisIdentities
    .map(identity => {
      return identity.gis_services.map(service => {
        return {
          ...service,
          // Ensure attributes is never null (API may return null for some services)
          attributes: service.attributes || {},
          token: identity?.arcgis_token || undefined,
        };
      });
    })
    .flat()
    .sort(gisServiceSort)
    .map(service => {
      // Add a new layerIndex property to maintain this exact order.
      return {
        ...service,
        layerIndex: layerIndex++,
      };
    });

  return gisServices;
}
